import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, FormFeedback, Button } from 'reactstrap';
import { fetchFormData } from '../../../../Api/ApiMethods';
import { shouldToastError } from '../../../../Api/utils';
import { toast } from 'react-toastify';
import FilesComponent from '../../../Pages/CallForms/Component/FilesComponent';
import PropTypes from 'prop-types';
import { validateDateYyyyMmDd } from '../../../../_helper/regexHandler';
import { useModalCrudState } from '../../../../_helper/hooks/useModalCrudState';

const toLocalISOStringDateOnly = (date) => {
    if (!date) return '';
    try {
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) return '';
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    } catch {
        return '';
    }
};

const PowerOfAttorneyDataModal = ({ isOpen, toggle, mode, selectedPowerOfAttorney, refreshData, customerId }) => {
    const initialFormState = useMemo(() => ({
        poaNumber: '',
        poaDate: '',
        isPoaTimeLimited: false,
        poaEndDate: '',
        notaryOfPoa: '',
        isLicensePlatesLimited: false,
        licensePlateLimit: [],
        isAccidentDateLimited: false,
        licensePlateLimitAccidentDate: '',
        poaOriginalArrived: false,
        customer: customerId
    }), [customerId]);

    const customHandlers = useMemo(() => ({
        isPoaTimeLimited: (value, prev) => ({
            ...prev,
            isPoaTimeLimited: value === 'true' || value === true,
            ...(value === 'false' || value === false ? { poaEndDate: '' } : {}),
        }),
        isLicensePlatesLimited: (value, prev) => ({
            ...prev,
            isLicensePlatesLimited: value === 'true' || value === true,
            ...(value === 'false' || value === false ? {
                licensePlateLimit: [],
                isAccidentDateLimited: false,
                licensePlateLimitAccidentDate: '',
            } : {}),
        }),
        isAccidentDateLimited: (value, prev) => ({
            ...prev,
            isAccidentDateLimited: value === 'true' || value === true,
            ...(value === 'false' || value === false ? { licensePlateLimitAccidentDate: '' } : {}),
        }),
        poaOriginalArrived: (value, prev) => ({
            ...prev,
            poaOriginalArrived: value === 'true' || value === true,
        }),
    }), []);

    const validationSchema = useMemo(() => ({
        poaDate: { required: true, message: 'Vekalet tarihi zorunludur.' },
        poaEndDate: {
            when: (fd) => fd.isPoaTimeLimited,
            required: true,
            message: 'Süreli vekalet için bitiş tarihi zorunludur.',
            custom: (value, fd) => {
                if (fd.poaDate && new Date(value) < new Date(fd.poaDate)) {
                    return 'Bitiş tarihi, vekalet tarihinden önce olamaz.';
                }
                return null;
            },
        },
        notaryOfPoa: { minLength: 3, minLengthMessage: 'Noter adı en az 3 karakter olmalıdır.' },
        licensePlateLimit: {
            when: (fd) => fd.isLicensePlatesLimited,
            required: true,
            message: 'Plaka sınırlaması seçildiğinde en az bir plaka eklenmelidir.',
        },
        licensePlateLimitAccidentDate: {
            when: (fd) => fd.isLicensePlatesLimited && fd.isAccidentDateLimited,
            required: true,
            message: 'Kaza tarihi sınırlaması seçildiğinde tarih zorunludur.',
        },
    }), []);

    const poaApiEndpoint = `${process.env.REACT_APP_LACIVERT_API_ENDPOINT}/powerofattorney`;

    const {
        formData,
        setFormData,
        errors,
        setErrors,
        handleCancel: handleCrudCancel,
        handleInputChange,
        handleSubmit,
        isSubmitting,
    } = useModalCrudState({
        isOpen,
        mode,
        selectedItem: selectedPowerOfAttorney,
        initialData: initialFormState,
        toggle,
        mapSelectedToData: ({ initialData, selectedItem }) => {
            const plateIds = Array.isArray(selectedItem?.licensePlateLimit)
                ? selectedItem.licensePlateLimit
                : selectedItem?.licensePlateLimit ? [selectedItem.licensePlateLimit] : [];

            const formattedPoaDate = toLocalISOStringDateOnly(selectedItem?.poaDate);
            const formattedPoaEndDate = toLocalISOStringDateOnly(selectedItem?.poaEndDate);
            const formattedAccidentDate = toLocalISOStringDateOnly(selectedItem?.licensePlateLimitAccidentDate);

            return {
                ...initialData,
                ...selectedItem,
                customer: selectedItem?.customer || customerId,
                poaDate: formattedPoaDate,
                poaEndDate: formattedPoaEndDate,
                licensePlateLimitAccidentDate: formattedAccidentDate,
                licensePlateLimit: plateIds.map(id => String(id)),
            };
        },
        normalizeInput: true,
        customHandlers,
        validationSchema,
        submitConfig: {
            apiEndpoint: poaApiEndpoint,
            refreshData,
            componentName: 'Vekaletname',
            onSuccess: (result) => {
                if (result?.isSuccess && result?.data?.id) {
                    setCurrentPoaId(result.data.id);
                }
                handleModalClose();
            },
            buildPayload: (data) => {
                const formatIsoDate = (dateString) => {
                    if (!validateDateYyyyMmDd(dateString)) return undefined;
                    try {
                        return new Date(dateString).toISOString().split('T')[0];
                    } catch {
                        return undefined;
                    }
                };

                const payload = {
                    customer: customerId,
                    poaDate: formatIsoDate(data.poaDate),
                    isPoaTimeLimited: Boolean(data.isPoaTimeLimited),
                    poaEndDate: data.isPoaTimeLimited ? formatIsoDate(data.poaEndDate) : undefined,
                    notaryOfPoa: data.notaryOfPoa || "",
                    isLicensePlatesLimited: Boolean(data.isLicensePlatesLimited),
                    licensePlateLimit: data.isLicensePlatesLimited ? (data.licensePlateLimit || []) : [],
                    isAccidentDateLimited: data.isLicensePlatesLimited ? Boolean(data.isAccidentDateLimited) : false,
                    licensePlateLimitAccidentDate: data.isLicensePlatesLimited && data.isAccidentDateLimited
                        ? formatIsoDate(data.licensePlateLimitAccidentDate)
                        : undefined,
                    poaOriginalArrived: Boolean(data.poaOriginalArrived),
                    ...(mode === 'modify' && selectedPowerOfAttorney?.id && { id: selectedPowerOfAttorney.id })
                };

                if (data.poaNumber) {
                    payload.poaNumber = data.poaNumber;
                }

                return payload;
            },
        },
    });

    const [selectedPlates, setSelectedPlates] = useState([]);
    const [availablePlates, setAvailablePlates] = useState([]);
    const [selectedPlateId, setSelectedPlateId] = useState('');
    const [currentPoaId, setCurrentPoaId] = useState(null);

    useEffect(() => {
        const loadData = async () => {
            if (isOpen) {
                if (mode === 'modify' && selectedPowerOfAttorney) {
                    try {
                        const plates = [];
                        const plateIds = Array.isArray(selectedPowerOfAttorney.licensePlateLimit)
                            ? selectedPowerOfAttorney.licensePlateLimit
                            : selectedPowerOfAttorney.licensePlateLimit ? [selectedPowerOfAttorney.licensePlateLimit] : [];

                        if (plateIds.length > 0) {
                            const platePromises = plateIds.map(plateId =>
                                fetchFormData({
                                    apiEndpoint: `${process.env.REACT_APP_LACIVERT_API_ENDPOINT}/licenseplate/${plateId}`,
                                    page: 1,
                                    pageSize: 1
                                }).catch(() => null)
                            );
                            const plateResponses = await Promise.all(platePromises);
                            plateResponses.forEach(response => {
                                const plateData = response?.data || response;
                                if (plateData) {
                                    plates.push(plateData);
                                }
                            });
                        }
                        setSelectedPlates(plates);
                        setCurrentPoaId(selectedPowerOfAttorney.id);
                    } catch (error) {
                        if (shouldToastError(error)) {
                            toast.error('Vekaletname bilgileri yüklenirken bir hata oluştu.');
                        }
                        setSelectedPlates([]);
                        setCurrentPoaId(null);
                    }
                } else {
                    setSelectedPlates([]);
                    setAvailablePlates([]);
                    setCurrentPoaId(null);
                }
                setSelectedPlateId('');
            }
        };

        loadData();
    }, [mode, selectedPowerOfAttorney, isOpen]);

    useEffect(() => {
        const loadCustomerPlates = async () => {
            if (formData.isLicensePlatesLimited && isOpen && customerId) {
                try {
                    const apiEndpoint = `${process.env.REACT_APP_LACIVERT_API_ENDPOINT}/licenseplate?customer=${customerId}`;
                    const response = await fetchFormData({
                        apiEndpoint: apiEndpoint,
                        page: 1,
                        pageSize: 1000
                    });

                    const platesData = response?.data?.items || response?.items || (Array.isArray(response) ? response : []);
                    setAvailablePlates(platesData);
                } catch (error) {
                    if (shouldToastError(error)) {
                        toast.error('Müşterinin plakaları yüklenirken bir hata oluştu.');
                    }
                    setAvailablePlates([]);
                }
            } else {
                setAvailablePlates([]);
            }
        };

        loadCustomerPlates();
    }, [formData.isLicensePlatesLimited, customerId, isOpen]);

    const handleModalClose = () => {
        setSelectedPlates([]);
        setAvailablePlates([]);
        setSelectedPlateId('');
        setCurrentPoaId(null);
        handleCrudCancel();
    };

    const handleFileSelectionChange = useCallback(() => {}, []);

    const handleAddPlate = () => {
        if (!selectedPlateId) {
            toast.warning('Lütfen eklenecek plakayı seçin.');
            return;
        }

        const plateToAdd = availablePlates.find(p => p.id === selectedPlateId);
        if (plateToAdd && !selectedPlates.some(p => p.id === plateToAdd.id)) {
            setSelectedPlates(prev => [...prev, plateToAdd]);
            setFormData(prev => ({
                ...prev,
                licensePlateLimit: [...(prev.licensePlateLimit || []), String(plateToAdd.id)]
            }));
            setSelectedPlateId('');
            if (errors.licensePlateLimit) {
                setErrors(prev => ({ ...prev, licensePlateLimit: undefined }));
            }
        } else if (selectedPlates.some(p => p.id === selectedPlateId)) {
            toast.info('Bu plaka zaten eklenmiş.');
        } else {
            toast.error('Seçilen plaka bulunamadı.');
        }
    };

    const handlePlateRemove = (plateIdToRemove) => {
        const stringPlateId = String(plateIdToRemove);
        setSelectedPlates(prev => prev.filter(p => String(p.id) !== stringPlateId));
        setFormData(prev => ({
            ...prev,
            licensePlateLimit: (prev.licensePlateLimit || []).filter(id => String(id) !== stringPlateId)
        }));
    };

    return (
        <Modal isOpen={isOpen} toggle={handleModalClose} size="lg" backdrop="static">
            <ModalHeader toggle={handleModalClose}>
                {mode === 'add' ? 'Vekaletname Ekle' : 'Vekaletname Düzenle'}
            </ModalHeader>
            <ModalBody>
                <Form>
                    <FormGroup>
                        <Label for="poaNumber">Vekalet Numarası</Label>
                        <Input
                            id="poaNumber"
                            name="poaNumber"
                            value={formData.poaNumber || ''}
                            onChange={handleInputChange}
                            placeholder="Vekalet Numarası (Opsiyonel)"
                            disabled={true}
                            invalid={!!errors.poaNumber}
                        />
                        <FormFeedback>{errors.poaNumber}</FormFeedback>
                    </FormGroup>

                    <FormGroup>
                        <Label for="poaDate">Vekalet Tarihi <span className="text-danger">*</span></Label>
                        <Input
                            type="date"
                            id="poaDate"
                            name="poaDate"
                            value={formData.poaDate || ''}
                            onChange={handleInputChange}
                            invalid={!!errors.poaDate}
                        />
                        <FormFeedback>{errors.poaDate}</FormFeedback>
                    </FormGroup>

                    <FormGroup className="d-flex align-items-center mb-3">
                        <Label htmlFor="isPoaTimeLimited" className="me-3 mb-0" style={{ flexShrink: 0, minWidth: '180px' }}>Vekalet Süreli Mi?</Label>
                        <div className="d-flex flex-grow-1">
                            <div className="form-check form-check-inline radio radio-success me-3">
                                <Input
                                    className="form-check-input"
                                    id="isPoaTimeLimitedTrue"
                                    type="radio"
                                    name="isPoaTimeLimited"
                                    value="true"
                                    checked={formData.isPoaTimeLimited === true}
                                    onChange={handleInputChange}
                                />
                                <Label className="form-check-label" htmlFor="isPoaTimeLimitedTrue">Evet</Label>
                            </div>
                            <div className="form-check form-check-inline radio radio-danger">
                                <Input
                                    className="form-check-input"
                                    id="isPoaTimeLimitedFalse"
                                    type="radio"
                                    name="isPoaTimeLimited"
                                    value="false"
                                    checked={formData.isPoaTimeLimited === false}
                                    onChange={(e) => {
                                        handleInputChange(e);
                                        setErrors(prev => ({ ...prev, poaEndDate: null }));
                                    }}
                                />
                                <Label className="form-check-label" htmlFor="isPoaTimeLimitedFalse">Hayır</Label>
                            </div>
                        </div>
                    </FormGroup>

                    {formData.isPoaTimeLimited && (
                        <FormGroup>
                            <Label for="poaEndDate">Vekalet Bitiş Tarihi <span className="text-danger">*</span></Label>
                            <Input
                                type="date"
                                id="poaEndDate"
                                name="poaEndDate"
                                value={formData.poaEndDate || ''}
                                onChange={handleInputChange}
                                invalid={!!errors.poaEndDate}
                                min={formData.poaDate}
                            />
                            <FormFeedback>{errors.poaEndDate}</FormFeedback>
                        </FormGroup>
                    )}

                    <FormGroup>
                        <Label for="notaryOfPoa">Noter</Label>
                        <Input
                            id="notaryOfPoa"
                            name="notaryOfPoa"
                            value={formData.notaryOfPoa || ''}
                            onChange={handleInputChange}
                            placeholder="Noter Adı (Örn: Ankara 15. Noter) (Opsiyonel)"
                            invalid={!!errors.notaryOfPoa}
                        />
                        <FormFeedback>{errors.notaryOfPoa}</FormFeedback>
                    </FormGroup>

                    <FormGroup className="d-flex align-items-center mb-3">
                        <Label htmlFor="isLicensePlatesLimited" className="me-3 mb-0" style={{ flexShrink: 0, minWidth: '180px' }}>Plaka Sınırlaması Var Mı?</Label>
                        <div className="d-flex flex-grow-1">
                            <div className="form-check form-check-inline radio radio-success me-3">
                                <Input
                                    className="form-check-input"
                                    id="isLicensePlatesLimitedTrue"
                                    type="radio"
                                    name="isLicensePlatesLimited"
                                    value="true"
                                    checked={formData.isLicensePlatesLimited === true}
                                    onChange={handleInputChange}
                                />
                                <Label className="form-check-label" htmlFor="isLicensePlatesLimitedTrue">Evet</Label>
                            </div>
                            <div className="form-check form-check-inline radio radio-danger">
                                <Input
                                    className="form-check-input"
                                    id="isLicensePlatesLimitedFalse"
                                    type="radio"
                                    name="isLicensePlatesLimited"
                                    value="false"
                                    checked={formData.isLicensePlatesLimited === false}
                                    onChange={(e) => {
                                        handleInputChange(e);
                                        setSelectedPlates([]);
                                        setErrors(prev => ({
                                            ...prev,
                                            licensePlateLimit: null,
                                            licensePlateLimitAccidentDate: null,
                                        }));
                                    }}
                                />
                                <Label className="form-check-label" htmlFor="isLicensePlatesLimitedFalse">Hayır</Label>
                            </div>
                        </div>
                    </FormGroup>

                    {formData.isLicensePlatesLimited && (
                        <>
                            <FormGroup>
                                <Label for="licensePlateSelect">Sınırlı Plakalar {selectedPlates.length > 0 ? `(${selectedPlates.length} adet)`: ''} <span className="text-danger">*</span></Label>
                                <div className="input-group">
                                    <Input
                                        type="select"
                                        id="licensePlateSelect"
                                        value={selectedPlateId}
                                        onChange={(e) => setSelectedPlateId(e.target.value)}
                                        className="form-select"
                                        disabled={availablePlates.length === 0}
                                    >
                                        <option value="">{availablePlates.length === 0 ? 'Müşteri plakaları yükleniyor/yok...' : 'Plaka Seçin...'}</option>
                                        {availablePlates
                                            .filter(plate => !selectedPlates.some(p => String(p.id) === String(plate.id)))
                                            .map(plate => (
                                                <option key={plate.id} value={String(plate.id)}>
                                                    {plate.licensePlateNumber} {plate.carBrand ? `(${plate.carBrand})` : ''}
                                                </option>
                                            ))
                                        }
                                    </Input>
                                    <Button
                                        color="primary"
                                        onClick={handleAddPlate}
                                        disabled={!selectedPlateId}
                                    >
                                        Ekle
                                    </Button>
                                </div>
                                <FormFeedback style={{ display: errors.licensePlateLimit ? 'block' : 'none' }} id="licensePlateLimitFeedback">
                                    {errors.licensePlateLimit}
                                </FormFeedback>
                            </FormGroup>

                            {selectedPlates.length > 0 && (
                                <div className="selected-plates mb-3 border p-2 rounded bg-light">
                                    {selectedPlates.map(plate => (
                                        <div
                                            key={plate.id}
                                            className="d-flex justify-content-between align-items-center py-1 border-bottom"
                                        >
                                            <span className="text-dark">
                                                {plate.licensePlateNumber} {plate.carBrand ? `- ${plate.carBrand}` : ''} {plate.carModel || ''}
                                            </span>
                                            <Button
                                                size="sm"
                                                color="danger"
                                                outline
                                                onClick={() => handlePlateRemove(plate.id)}
                                                aria-label={`${plate.licensePlateNumber} plakasını kaldır`}
                                            >
                                                &times;
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <FormGroup className="d-flex align-items-center mb-3">
                                <Label htmlFor="isAccidentDateLimited" className="me-3 mb-0" style={{ flexShrink: 0, minWidth: '180px' }}>Kaza Tarihi Sınırlaması?</Label>
                                <div className="d-flex flex-grow-1">
                                    <div className="form-check form-check-inline radio radio-success me-3">
                                        <Input
                                            className="form-check-input"
                                            id="isAccidentDateLimitedTrue"
                                            type="radio"
                                            name="isAccidentDateLimited"
                                            value="true"
                                            checked={formData.isAccidentDateLimited === true}
                                            onChange={handleInputChange}
                                        />
                                        <Label className="form-check-label" htmlFor="isAccidentDateLimitedTrue">Evet</Label>
                                    </div>
                                    <div className="form-check form-check-inline radio radio-danger">
                                        <Input
                                            className="form-check-input"
                                            id="isAccidentDateLimitedFalse"
                                            type="radio"
                                            name="isAccidentDateLimited"
                                            value="false"
                                            checked={formData.isAccidentDateLimited === false}
                                            onChange={(e) => {
                                                handleInputChange(e);
                                                setErrors(prev => ({ ...prev, licensePlateLimitAccidentDate: null }));
                                            }}
                                        />
                                        <Label className="form-check-label" htmlFor="isAccidentDateLimitedFalse">Hayır</Label>
                                    </div>
                                </div>
                            </FormGroup>

                            {formData.isAccidentDateLimited && (
                                <FormGroup>
                                    <Label for="licensePlateLimitAccidentDate">Plaka Kaza Tarihi <span className="text-danger">*</span></Label>
                                    <Input
                                        type="date"
                                        id="licensePlateLimitAccidentDate"
                                        name="licensePlateLimitAccidentDate"
                                        value={formData.licensePlateLimitAccidentDate || ''}
                                        onChange={handleInputChange}
                                        invalid={!!errors.licensePlateLimitAccidentDate}
                                    />
                                    <FormFeedback>{errors.licensePlateLimitAccidentDate}</FormFeedback>
                                </FormGroup>
                            )}
                        </>
                    )}

                    <FormGroup className="d-flex align-items-center mb-3">
                        <Label htmlFor="poaOriginalArrived" className="me-3 mb-0" style={{ flexShrink: 0, minWidth: '180px' }}>Aslı Geldi Mi?</Label>
                        <div className="d-flex flex-grow-1">
                            <div className="form-check form-check-inline radio radio-success me-3">
                                <Input
                                    className="form-check-input"
                                    id="poaOriginalArrivedTrue"
                                    type="radio"
                                    name="poaOriginalArrived"
                                    value="true"
                                    checked={formData.poaOriginalArrived === true}
                                    onChange={handleInputChange}
                                />
                                <Label className="form-check-label" htmlFor="poaOriginalArrivedTrue">Evet</Label>
                            </div>
                            <div className="form-check form-check-inline radio radio-danger">
                                <Input
                                    className="form-check-input"
                                    id="poaOriginalArrivedFalse"
                                    type="radio"
                                    name="poaOriginalArrived"
                                    value="false"
                                    checked={formData.poaOriginalArrived === false}
                                    onChange={handleInputChange}
                                />
                                <Label className="form-check-label" htmlFor="poaOriginalArrivedFalse">Hayır</Label>
                            </div>
                        </div>
                    </FormGroup>
                    <FormGroup className="mt-4">
                        <FilesComponent
                            onFilesChange={handleFileSelectionChange}
                            callFormReference={false}
                            referenceId={currentPoaId}
                            typeFilter={['VEKALETNAME']}
                            title="Vekaletname Belgeleri"
                            multiple={false}
                            canEdit={true}
                        />
                    </FormGroup>
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button color="success" outline onClick={handleSubmit} disabled={isSubmitting} className="rounded-pill">
                    {isSubmitting ? 'Kaydediliyor...' : (mode === 'add' ? 'Ekle' : 'Güncelle')}
                </Button>
                <Button color="danger" outline onClick={handleModalClose} disabled={isSubmitting} className="ms-2 rounded-pill">
                    İptal
                </Button>
            </ModalFooter>
        </Modal>
    );
};

PowerOfAttorneyDataModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    mode: PropTypes.oneOf(['add', 'modify']).isRequired,
    selectedPowerOfAttorney: PropTypes.object,
    refreshData: PropTypes.func.isRequired,
    customerId: PropTypes.string.isRequired,
};

export default PowerOfAttorneyDataModal;
